import { getCacheDurationForUrl } from "./cacheConfig";

// Store cache entries with timestamps
class CacheEntry {
  constructor(value, ttl) {
    this.value = value;
    this.ttl = ttl;
    this.timestamp = Date.now();
  }

  isExpired() {
    if (this.ttl === null) return false; // null ttl = never expire
    return Date.now() - this.timestamp > this.ttl;
  }
}

class SmartCache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
    this.subscribers = new Set();
  }

  // Subscribe to invalidation events. Returns unsubscribe fn.
  subscribe(fn) {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  _notify(pattern) {
    for (const fn of this.subscribers) {
      try {
        fn(pattern);
      } catch (err) {
        console.warn("cache subscriber failed:", err);
      }
    }
  }

  set(key, value, ttl) {
    // Clear existing timer if any
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }

    // Store with TTL
    this.cache.set(key, new CacheEntry(value, ttl));

    // Set auto-expiry timer if TTL exists
    if (ttl !== null) {
      const timer = setTimeout(() => {
        this.cache.delete(key);
        this.timers.delete(key);
      }, ttl);
      this.timers.set(key, timer);
    }
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    // Check if expired
    if (entry.isExpired()) {
      this.cache.delete(key);
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }
      return undefined;
    }

    return entry.value;
  }

  has(key) {
    return this.get(key) !== undefined;
  }

  delete(key) {
    this.cache.delete(key);
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
  }

  invalidate(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => this.delete(key));
    this._notify(pattern);
  }

  clear() {
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.cache.clear();
    this.timers.clear();
  }

  getStats() {
    return {
      size: this.cache.size,
      timers: this.timers.size,
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        expired: entry.isExpired(),
        ttl: entry.ttl,
      })),
    };
  }
}

// Global cache instances
export const resultCache = new SmartCache();
export const inflightMap = new Map();

// Create automatic cache invalidation on mutations
export function setupCacheInvalidation() {
  window.__invalidateCache = (pattern) => {
    resultCache.invalidate(pattern);
  };

  // Invalidate related caches after mutations
  window.__invalidateCaches = {
    afterLeaveUpdate: () => {
      resultCache.invalidate("leave");
      resultCache.invalidate("summary");
    },
    afterAttendanceUpdate: () => {
      resultCache.invalidate("attendance");
      resultCache.invalidate("stat");
    },
    afterEmployeeUpdate: () => {
      resultCache.invalidate("employee");
      resultCache.invalidate("department");
    },
    afterHolidayUpdate: () => {
      resultCache.invalidate("holiday");
    },
  };
}

export function clearApiCache() {
  resultCache.clear();
  inflightMap.clear();
}

// Get cache duration for a URL
export function getCacheTTL(url) {
  return getCacheDurationForUrl(url);
}
